import { Api } from '@blink/components';
import { NETWORK } from '@blink/components/src/constants/global';
import { objectToSnake } from 'ts-case-convert';

export const enum BorrowStatusType {
    OPENED = 'opened',
    SUSPENDED = 'suspended',
    REGISTERED = 'registered',
    CLOSED = 'closed',
    DRAFT = 'draft',
    PENDING = 'pending',
    EXCEPTION = 'exception',
    LIVE = 'live',
}

export type Token = {
    id?: number;
    apy?: string;
    totalDeposited?: string;
    address?: string;
    name: string;
    symbol: string;
    balance?: string;
    decimals: number;
    agreementAddress?: string;
    userBalance?: string;
    poolBalance?: string;
    price?: string;
};

export enum ROLE {
    BORROWER = 'borrower',
    LENDER = 'lender',
    OWNER = 'owner',
}

export enum TYPE {
    LEND = 'lend',
    BORROW = 'borrow',
}

export type HistoryItem = {
    effectiveDate: string;
    value: string;
};

export type AgreementBasic = {
    id: number;
    address: string;
    icon: string;
    leverageTokenId: number;
    status: BorrowStatusType;
    title: string;
    apy: string;
    role: ROLE;
    noWalletInfo: boolean;
    errorContract: boolean;
    version?: number;
};

export type AgreementDetails = {
    address: string;
    leverageTokenId: number;
    title: string;
    description: string;
    lenders: string[];
    borrowers: string[];
    liquidationFee: string;
    liquidationThreshold: string;
    marginCallThreshold: string;
    stressTestMatrix: StressTestMatrix[];
    apy: string;
    icon: string;
    noWalletInfo: boolean;
    errorContract: boolean;
};

export type Agreement = AgreementBasic &
    AgreementDetails & {
        totalDepositThreshold?: string;
        totalDeposited?: string;
        description?: string;
        leverage: Token;
        collaterals: any[];
        lenders?: string[];
        borrowers?: string[];
        tokens?: string[];
        totalPosition?: string;
        poolBalance?: string;
        withdrawable?: string;
        interestAccrued?: string;
        interestClaimable?: string;
        lpTokenBalance?: string;
        totalAssets?: string;
        assetsFetched?: string;
        totalProfit?: string;
        totalBorrowed?: string;
        poolUtilization?: string;
        userValue?: string;
    };

export type ClientAgreement = Agreement & {
    apy: HistoryItem[];
    leverage?: Token;
};

export type StressTestMatrix = {
    symbol: string;
    percent: string;
    address: string;
    priceProvider: string;
    priceModel: string;
};

export type Price = {
    price?: string;
};

export type MaintenanceData = {
    address: string;
    accruedInterest: string;
    stressTested: string;
    riskFactor: string;
};

export type History<T> = {
    channel: string;
    data: T;
    offset: number;
};

export type UserConversionRates = Token & Price;

export type UserBorrow = {
    address: string;
    leverage: {
        id: number;
        apy: string;
        address: string;
        name: string;
        symbol: string;
        decimals: number;
        amount: string;
    };
    maintenance: MaintenanceData;
    history: History<MaintenanceData>[];
    openedAt: string;
    agreementAddress: string;
    apy?: string;
};

export type GetAssetsToBorrowProps = {
    blockchainName?: string;
    walletAddress?: string;
    status?: BorrowStatusType;
    withoutCache?: boolean;
};

export interface Metadata {
    leverage: string;
    apy: number;
    totalDepositThreshold: bigint;
    collaterals: string[];
    liquidityProviders: string[];
    assetManagers: string[];
}

export interface State {
    tokens: string[];
    operators: string[];
}

export interface PoolRequest {
    apy: string;
    details: string;
    position: string;
    token: string;
    type: TYPE;
}

export type PoolRequestResponse = string | { result: string };

export type ClientPool = {
    id: number;
    address: string;
    apy: string;
    apyHistory: HistoryItem[];
    icon: string;
    version: number;
    leverageTokenId: number;
    leverageToken: string;
    leverage?: Token;
    status: BorrowStatusType;
    title: string;
    stressTestMatrix: StressTestMatrix[];
    borrowers: number[];
    borrowersWallets: string[];
    collateralTokens: string[];
    description: string;
    lenders: number[];
    lendersWallets: string[];
    liquidationFee: string;
    liquidationThreshold: string;
    marginCallThreshold: string;
    maxLoanToValue: string;
    totalDepositThreshold: string;
    whitelistedOperators: string[];
    whitelistedTokens: string[];
    withdrawalHaircut: string;
    withdrawalNBlockDelay: number;
    poolBalance?: string;
    withdrawable?: string;
    totalPosition?: string;
    interestAccrued?: string;
    interestClaimable?: string;
    lpTokenBalance?: string;
    totalAssets?: string;
    assetsFetched?: string;
    totalProfit?: string;
    totalDeposited?: string;
    totalBorrowed?: string;
    poolUtilization?: string;
};

export const getTokens = () => {
    return Api.getResponse<Token[]>(`${Api.REACT_APP_ANALYTICS_HTTP_URL}/v1/tokens`);
};

export const getAgreements = (withoutCache?: boolean) => {
    return Api.getResponse<AgreementBasic[]>(
        `${Api.ARKIS_PORTAL_HTTP_URL}/v1/pools`,
        undefined,
        withoutCache,
    );
};

export const getAgreementDetails = (address: string, withoutCache?: boolean) => {
    return Api.getResponse<AgreementDetails>(
        `${Api.ARKIS_PORTAL_HTTP_URL}/v1/pools/${address}`,
        undefined,
        withoutCache,
    );
};

export const getPrice = (base: string, quote: string, withoutCache = true) => {
    return Api.getResponse<Price>(
        `${Api.REACT_APP_ANALYTICS_HTTP_URL}/v2/prices/ethereum?base=${base}&quote=${quote}`,
        undefined,
        withoutCache,
    );
};

export const getPriceByProvider = (
    base: string,
    quote: string,
    provider: string,
    model: string,
    withoutCache = true,
) => {
    return Api.getResponse<Price>(
        `${Api.REACT_APP_ANALYTICS_HTTP_URL}/v2/prices/ethereum/${provider}/${model}?base=${base}&quote=${quote}`,
        undefined,
        withoutCache,
    );
};

export const getUserBorrows = ({
    walletAddress,
    status = BorrowStatusType.OPENED,
    withoutCache = false,
}: GetAssetsToBorrowProps) => {
    return Api.getResponse<UserBorrow[]>(
        `${Api.REACT_APP_ANALYTICS_HTTP_URL}/v1/margin-accounts?owner=${walletAddress}&status=${status}`,
        undefined,
        withoutCache,
    );
};

export type UserBorrowRiskFactorGraph = {
    id: number;
    symbol: string;
    amount: string;
    startApy: string;
    currentApy: string;
    blockchainName: NETWORK;
    marginAccountAddress: string;
    status: string;
    historicalRiskFactor: Array<UserBorrowHistoricalRisk>;
};

export type UserBorrowHistoricalRisk = {
    stressTested: string;
    riskFactor: string;
    accruedInterest: string;
    timestamp: number;
};

export const postPersonalisedPool = (body: PoolRequest) => {
    const snakeBody = objectToSnake(body);
    return Api.postResponse<PoolRequestResponse>(
        `${Api.ARKIS_PORTAL_HTTP_URL}/v1/pool-requests`,
        snakeBody,
    );
};

export const getClientPoolById = (id: string) => {
    return Api.getResponse<ClientPool>(`${Api.ARKIS_PORTAL_HTTP_URL}/v1/pools/${id}`);
};
